import React, { useEffect, useState } from "react";
import { Box, ButtonBase, InputBase, Typography } from "@mui/material";
import { Add, ChatBubbleOutline, Search } from "@mui/icons-material";
import { aiChatService, ConversationSummary } from "../../services/aiChatService";
import { formatWhen } from "../chat/AgentConversation";
import { listChipSx, listHeaderSx, listRowSx, listSearchSx } from "./AssistantUi";
import { colors } from "../../theme/designSystem";

export const CONVERSATION_PERIODS = [7, 15, 30] as const;
const SEARCH_DELAY_MS = 350;

/**
 * Histórico de conversas do kit dos Assistentes (RN-573): Nova conversa, busca no texto, período (7, 15 ou 30 dias)
 * e a lista. O mesmo componente no chat da Ani e nas áreas Agenda e Livro-caixa, com o espaçamento padrão da coluna
 * de lista (listHeaderSx, listSearchSx, listChipSx, listRowSx). `area` vazia = chat comum. `reloadKey` muda quando
 * uma conversa nova é criada ou recebe mensagem, para a lista acompanhar.
 */
export const ConversationHistory: React.FC<{
  agentId: string;
  area?: string;
  activeId: string | null;
  reloadKey?: unknown;
  onOpen: (conversation: ConversationSummary) => void;
  onNew: () => void;
}> = ({ agentId, area, activeId, reloadKey, onOpen, onNew }) => {
  const [days, setDays] = useState<number>(CONVERSATION_PERIODS[0]);
  const [search, setSearch] = useState("");
  const [items, setItems] = useState<ConversationSummary[] | null>(null);

  useEffect(() => {
    let alive = true;
    const timer = window.setTimeout(() => {
      aiChatService.conversations(agentId, { area, days, search })
        .then((list) => { if (alive) setItems(list); })
        .catch(() => { if (alive) setItems([]); });
    }, search ? SEARCH_DELAY_MS : 0);
    return () => { alive = false; window.clearTimeout(timer); };
  }, [agentId, area, days, search, reloadKey]);

  return (
    <Box sx={{ display: "flex", flexDirection: "column", minHeight: 0, height: "100%" }}>
      <Box sx={listHeaderSx}>
        <ButtonBase onClick={onNew}
          sx={{ justifyContent: "flex-start", gap: 1, px: 1.5, minHeight: 40, borderRadius: "22px", color: colors.primaryText, bgcolor: colors.primaryLight,
            fontWeight: 600, fontSize: "0.875rem", fontFamily: "inherit", "&:hover": { bgcolor: colors.primarySoft }, "&.Mui-focusVisible": { outline: `2px solid ${colors.primary}` } }}>
          <Add fontSize="small" aria-hidden />Nova conversa
        </ButtonBase>
        <Box sx={listSearchSx}>
          <Search sx={{ fontSize: 18, color: colors.textSecondary }} aria-hidden />
          <InputBase value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Buscar nas conversas"
            inputProps={{ "aria-label": "Buscar nas conversas" }} sx={{ flex: 1, fontSize: "0.875rem" }} />
        </Box>
        <Box role="radiogroup" aria-label="Período" sx={{ display: "flex", gap: 0.75 }}>
          {CONVERSATION_PERIODS.map((d) => (
            <ButtonBase key={d} role="radio" aria-checked={d === days} onClick={() => setDays(d)} sx={listChipSx(d === days)}>
              {d} dias
            </ButtonBase>
          ))}
        </Box>
      </Box>

      <Box role="list" aria-label="Conversas" sx={{ flex: 1, minHeight: 0, overflowY: "auto", pb: 1 }}>
        {items !== null && items.length === 0 && (
          <Typography sx={{ color: colors.textSecondary, fontSize: "0.8125rem", textAlign: "center", py: 3, px: 2 }}>
            {search ? "Nenhuma conversa com esse texto no período." : `Nenhuma conversa nos últimos ${days} dias.`}
          </Typography>
        )}
        {(items ?? []).map((c) => {
          const active = c.id === activeId;
          return (
            <ButtonBase key={c.id} role="listitem" onClick={() => onOpen(c)} aria-current={active ? "true" : undefined} sx={listRowSx(active)}>
              <ChatBubbleOutline sx={{ fontSize: 18, color: active ? colors.primaryText : colors.textSecondary, flexShrink: 0 }} aria-hidden />
              <Box sx={{ minWidth: 0, flex: 1 }}>
                <Typography sx={{ fontSize: "0.875rem", color: colors.textStrong, fontWeight: active ? 600 : 400 }} noWrap>{c.title || "Conversa"}</Typography>
                <Typography sx={{ fontSize: "0.75rem", color: colors.textSecondary }}>{formatWhen(c.updatedAt)}</Typography>
              </Box>
            </ButtonBase>
          );
        })}
      </Box>
    </Box>
  );
};
